import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, CheckCircle2, Undo2, FileText, ArrowUp, ArrowDown, ArrowUpDown, Upload } from "lucide-react";
import InvoiceImportModal from "@/components/InvoiceImportModal";

const STATUS_LABEL = { open: "Açık", partial: "Kısmi", paid: "Ödendi", cancelled: "İptal" };
const STATUS_COLOR = {
  open: "text-neutral-700 border-neutral-300",
  partial: "text-amber-800 border-amber-300 bg-amber-50",
  paid: "text-emerald-700 border-emerald-300 bg-emerald-50",
  cancelled: "text-neutral-500 border-neutral-300 bg-neutral-50 line-through",
};

export default function Invoices() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const drillContactId = params.get("contact") || "";
  const highlightId = params.get("hl") || "";

  const [tab, setTab] = useState("sales"); // sales | purchase
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ field: "due_date", dir: "asc" });
  const [importOpen, setImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const highlightRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data: inv }, { data: co }, { data: ac }, { data: sm }] = await Promise.all([
      api.get(`/invoices?direction=${tab}`),
      api.get("/contacts"),
      api.get("/accounts"),
      api.get("/invoices/summary"),
    ]);
    setItems(inv); setContacts(co); setAccounts(ac); setSummary(sm);
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  // Drill-down: if URL has ?hl=X, find the invoice's direction and switch tab, then scroll+highlight.
  useEffect(() => {
    if (!highlightId || !items.length) return;
    const target = items.find((i) => i.id === highlightId);
    if (!target) {
      // maybe it's on the other tab
      // Try fetching the other tab
      api.get(`/invoices?direction=${tab === "sales" ? "purchase" : "sales"}`).then(({ data }) => {
        if (data.find((i) => i.id === highlightId)) {
          setTab(tab === "sales" ? "purchase" : "sales");
        }
      });
      return;
    }
    // set search filter to invoice number to make it prominent even without highlight scroll
    setQ(target.invoice_number);
    setTimeout(() => {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 200);
  }, [items, highlightId, tab]);

  const remove = async (id) => {
    if (!window.confirm("Fatura silinsin mi? (Ödenmişse bağlı işlem de silinir)")) return;
    await api.delete(`/invoices/${id}`); toast.success("Silindi"); load();
  };
  const undoPay = async (id) => {
    if (!window.confirm("Fatura ödemesi geri alınsın mı? (Bağlı gelir/gider işlemi silinir)")) return;
    await api.post(`/invoices/${id}/undo-pay`); toast.success("Ödeme geri alındı"); load();
  };

  // Sekme değişiminde seçim resetlensin
  useEffect(() => { setSelectedIds(new Set()); }, [tab]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visibleIds) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} fatura silinsin mi? Bağlı ödeme/tahsilat işlemleri de silinir. Bu işlem geri alınamaz.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids });
      toast.success(`${data.deleted_invoices} fatura + ${data.deleted_transactions} işlem silindi`);
      clearSelection();
      load();
    } catch (e) {
      toast.error("Silme hatası: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setBulkDeleting(false);
    }
  };

  const contactNames = useMemo(
    () => Object.fromEntries(contacts.map((c) => [c.id, c.name])), [contacts]
  );

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) return { field, dir: field === "amount" ? "desc" : "asc" };
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLocaleLowerCase("tr");
    let arr = items;
    if (query) {
      arr = arr.filter((i) => {
        const cname = (contactNames[i.contact_id] || "").toLocaleLowerCase("tr");
        return i.invoice_number.toLocaleLowerCase("tr").includes(query) || cname.includes(query);
      });
    }
    const getKey = (i) => {
      switch (sort.field) {
        case "number": return i.invoice_number.toLocaleLowerCase("tr");
        case "contact": return (contactNames[i.contact_id] || "").toLocaleLowerCase("tr");
        case "issue_date": return i.issue_date;
        case "amount": return Number(i.amount) || 0;
        case "status": return i.status;
        default: return i.due_date;
      }
    };
    return [...arr].sort((a, b) => {
      const av = getKey(a), bv = getKey(b);
      let cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv), "tr");
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [items, q, sort, contactNames]);

  const isSales = tab === "sales";
  const s = summary || {};

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Cari Borç & Alacak</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Faturalar</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="invoice-import-open"
            onClick={() => setImportOpen(true)}
            className="brutal-btn bg-white text-black border border-black px-4 py-2.5 text-sm flex items-center gap-2"
            title={`CSV/Excel'den ${tab === "sales" ? "satış" : "alış"} faturası yükle`}
          >
            <Upload size={16} /> CSV'den Aktar
          </button>
          <button
            data-testid="invoice-add-button"
            onClick={() => { setEditing(null); setOpen(true); }}
            disabled={contacts.length === 0}
            className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2 disabled:opacity-40"
            title={contacts.length === 0 ? "Önce bir cari ekleyin" : "Yeni fatura"}
          >
            <Plus size={16} /> Yeni Fatura
          </button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Toplam Borç (Açık Satış)" value={s.receivable_open_amount} tone="emerald"
              subtitle={`${s.receivable_open_count || 0} fatura`} tid="stat-inv-recv" />
        <Stat label="Vadesi Geçmiş Borç" value={s.receivable_overdue_amount}
              tone={(s.receivable_overdue_count || 0) > 0 ? "rose" : ""}
              subtitle={`${s.receivable_overdue_count || 0} fatura`} tid="stat-inv-recv-od" />
        <Stat label="Toplam Alacak (Açık Alış)" value={s.payable_open_amount} tone="rose"
              subtitle={`${s.payable_open_count || 0} fatura`} tid="stat-inv-pay" />
        <Stat label="Vadesi Geçmiş Alacak" value={s.payable_overdue_amount}
              tone={(s.payable_overdue_count || 0) > 0 ? "rose" : ""}
              subtitle={`${s.payable_overdue_count || 0} fatura`} tid="stat-inv-pay-od" />
      </div>

      <div className="flex items-center gap-2 border-b border-black/10">
        {[["sales", "Satış (Borç)"], ["purchase", "Alış (Alacak)"]].map(([k, l]) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm uppercase tracking-widest border-b-2 -mb-px transition-colors ${
              tab === k ? "border-black text-black font-semibold" : "border-transparent text-neutral-500 hover:text-black"
            }`}
          >{l}</button>
        ))}
        <input
          data-testid="invoice-search"
          placeholder="Fatura no veya cari ara…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="ml-auto min-w-[240px] border border-black/20 px-3 py-1.5 outline-none focus:ring-2 focus:ring-black text-sm"
        />
      </div>

      {selectedIds.size > 0 && (
        <div className="brutal-card bg-amber-50 border-amber-300 flex items-center justify-between px-4 py-2.5" data-testid="invoice-bulk-toolbar">
          <div className="text-sm">
            <span className="font-semibold" data-testid="invoice-selected-count">{selectedIds.size}</span> fatura seçildi
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              data-testid="invoice-bulk-clear"
              className="text-xs uppercase tracking-widest px-3 py-1.5 border border-black/30 hover:bg-white"
            >Seçimi Temizle</button>
            <button
              onClick={bulkDelete}
              disabled={bulkDeleting}
              data-testid="invoice-bulk-delete"
              className="text-xs uppercase tracking-widest px-3 py-1.5 bg-rose-600 text-white hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5"
            >
              <Trash2 size={13} /> {bulkDeleting ? "Siliniyor…" : `${selectedIds.size} Faturayı Sil`}
            </button>
          </div>
        </div>
      )}

      <div className="brutal-card overflow-hidden">
        <table className="w-full text-sm" data-testid="invoices-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  data-testid="invoice-select-all"
                  checked={filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id))}
                  onChange={() => toggleSelectAll(filtered.map((i) => i.id))}
                  className="cursor-pointer"
                />
              </th>
              <SortTh field="number" label="Fatura No" sort={sort} onToggle={toggleSort} tid="sort-inv-number" />
              <SortTh field="contact" label="Cari" sort={sort} onToggle={toggleSort} tid="sort-inv-contact" />
              <SortTh field="issue_date" label="Tarih" sort={sort} onToggle={toggleSort} tid="sort-inv-issue" />
              <SortTh field="due_date" label="Vade" sort={sort} onToggle={toggleSort} tid="sort-inv-due" />
              <SortTh field="amount" label="Tutar" sort={sort} onToggle={toggleSort} align="right" tid="sort-inv-amount" />
              <th className="text-right px-4 py-3 label-caps">Kalan</th>
              <SortTh field="status" label="Durum" sort={sort} onToggle={toggleSort} tid="sort-inv-status" />
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-neutral-500 text-sm">
                {isSales ? "Satış faturası yok." : "Alış faturası yok."}
              </td></tr>
            )}
            {filtered.map((i) => {
              const overdue = (i.status === "open" || i.status === "partial") && i.due_date < todayISO();
              const remaining = Math.max(0, (i.amount || 0) - (i.paid_amount || 0));
              return (
                <tr key={i.id} className={`border-b border-black/5 hover:bg-neutral-50 ${selectedIds.has(i.id) ? "bg-amber-50" : ""}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      data-testid={`invoice-select-${i.id}`}
                      checked={selectedIds.has(i.id)}
                      onChange={() => toggleSelect(i.id)}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3 font-mono-num font-medium">{i.invoice_number}</td>
                  <td className="px-4 py-3">{contactNames[i.contact_id] || "—"}</td>
                  <td className="px-4 py-3 font-mono-num text-neutral-600">{fmtDate(i.issue_date)}</td>
                  <td className={`px-4 py-3 font-mono-num ${overdue ? "text-rose-600 font-semibold" : "text-neutral-600"}`}>
                    {fmtDate(i.due_date)}
                    {overdue && <div className="text-[10px] uppercase tracking-widest">Gecikti</div>}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono-num font-semibold ${isSales ? "text-emerald-700" : "text-rose-600"}`}>
                    {fmtTRY(i.amount)}
                  </td>
                  <td
                    data-testid={`invoice-remaining-${i.id}`}
                    className={`px-4 py-3 text-right font-mono-num ${
                      i.status === "paid" ? "text-neutral-400" : i.status === "partial" ? "text-amber-800 font-semibold" : "text-neutral-700"
                    }`}
                  >
                    {i.status === "paid" ? "—" : fmtTRY(remaining)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      data-testid={`invoice-status-${i.id}`}
                      className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_COLOR[i.status]}`}
                    >
                      {STATUS_LABEL[i.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {(i.status === "open" || i.status === "partial") && (
                      <button
                        data-testid={`invoice-pay-${i.id}`}
                        onClick={() => setPayingId(i.id)}
                        className="border border-black bg-black text-white px-3 py-1 text-xs uppercase tracking-widest hover:opacity-90 mr-1 inline-flex items-center gap-1"
                        title={isSales ? "Tahsil Edildi" : "Ödendi"}
                      >
                        <CheckCircle2 size={12} /> {isSales ? "Tahsil" : "Öde"}
                      </button>
                    )}
                    {i.status === "paid" && (
                      <button
                        data-testid={`invoice-undo-${i.id}`}
                        onClick={() => undoPay(i.id)}
                        className="p-1.5 hover:bg-neutral-100 mr-1"
                        title="Ödemeyi Geri Al"
                      ><Undo2 size={13} /></button>
                    )}
                    <button
                      data-testid={`invoice-edit-${i.id}`}
                      onClick={() => { setEditing(i); setOpen(true); }}
                      className="p-1.5 hover:bg-neutral-100"><Edit3 size={13} /></button>
                    <button
                      data-testid={`invoice-delete-${i.id}`}
                      onClick={() => remove(i.id)}
                      className="p-1.5 hover:bg-rose-50"><Trash2 size={13} className="text-rose-600" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <InvoiceModal
          defaultDirection={tab}
          contacts={contacts}
          editing={editing}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
      {payingId && (
        <PayModal
          invoice={items.find((x) => x.id === payingId)}
          contactName={contactNames[items.find((x) => x.id === payingId)?.contact_id]}
          accounts={accounts}
          onClose={() => setPayingId(null)}
          onSaved={() => { setPayingId(null); load(); }}
        />
      )}
      {importOpen && (
        <InvoiceImportModal
          direction={tab}
          onClose={() => setImportOpen(false)}
          onImported={() => load()}
        />
      )}
    </div>
  );
}

function Stat({ label, value, tone, tid, subtitle }) {
  const color = tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className="brutal-card p-4" data-testid={tid}>
      <div className="label-caps">{label}</div>
      <div className={`font-mono-num text-xl md:text-2xl font-semibold mt-2 ${color}`}>{fmtTRY(value || 0)}</div>
      {subtitle && <div className="text-[11px] text-neutral-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function SortTh({ field, label, sort, onToggle, align, tid }) {
  const isActive = sort.field === field;
  const Icon = !isActive ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"}`}>
      <button type="button" data-testid={tid} onClick={() => onToggle(field)}
        className={`label-caps inline-flex items-center gap-1 select-none ${isActive ? "text-black" : "text-neutral-500 hover:text-black"} ${align === "right" ? "flex-row-reverse" : ""}`}>
        <span>{label}</span>
        <Icon size={11} strokeWidth={2.2} className={isActive ? "opacity-100" : "opacity-50"} />
      </button>
    </th>
  );
}

function InvoiceModal({ defaultDirection, contacts, editing, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    direction: defaultDirection,
    contact_id: contacts[0]?.id || "",
    invoice_number: "",
    issue_date: todayISO(),
    due_date: todayISO(),
    amount: "",
    notes: "",
  });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      direction: form.direction,
      contact_id: form.contact_id,
      invoice_number: form.invoice_number.trim(),
      issue_date: form.issue_date,
      due_date: form.due_date,
      amount: Number(form.amount) || 0,
      notes: form.notes || "",
    };
    if (!payload.contact_id) { toast.error("Cari seçin"); return; }
    if (!payload.invoice_number) { toast.error("Fatura numarası girin"); return; }
    if (payload.amount <= 0) { toast.error("Tutar 0'dan büyük olmalı"); return; }
    try {
      if (editing) await api.put(`/invoices/${editing.id}`, payload);
      else await api.post("/invoices", payload);
      toast.success("Kaydedildi"); onSaved();
    } catch { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="invoice-form-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-black w-full max-w-lg"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="label-caps">Fatura</div>
            <h2 className="font-display text-xl font-bold">{editing ? "Faturayı Düzenle" : "Yeni Fatura"}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <F label="Fatura Yönü">
            <div className="grid grid-cols-2 gap-2">
              {[["sales", "Satış (Borç)"], ["purchase", "Alış (Alacak)"]].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`inv-direction-${k}`}
                  onClick={() => setForm({ ...form, direction: k })}
                  className={`py-2 text-xs uppercase tracking-widest border ${
                    form.direction === k ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                  }`}
                >{l}</button>
              ))}
            </div>
          </F>
          <F label="Cari">
            <select
              required data-testid="inv-contact"
              value={form.contact_id}
              onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
              className="input bg-white"
            >
              <option value="">— Seçin —</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Fatura Numarası">
              <input
                required data-testid="inv-number"
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                className="input font-mono-num"
                placeholder="ABC2026-0001"
              />
            </F>
            <F label="Tutar (₺)">
              <input
                required type="number" step="0.01" min="0.01" data-testid="inv-amount"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="input font-mono-num text-lg"
                placeholder="0,00"
              />
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Fatura Tarihi">
              <input
                required type="date" data-testid="inv-issue"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                className="input"
              />
            </F>
            <F label="Vade Tarihi">
              <input
                required type="date" data-testid="inv-due"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="input"
              />
            </F>
          </div>
          <F label="Notlar (opsiyonel)">
            <textarea
              data-testid="inv-notes"
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="input"
            />
          </F>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="invoice-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">Kaydet</button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function PayModal({ invoice, contactName, accounts, onClose, onSaved }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || "");
  const [payDate, setPayDate] = useState(todayISO());

  const save = async (e) => {
    e.preventDefault();
    if (!accountId) { toast.error("Ödeme yapılacak hesap seçin"); return; }
    try {
      await api.post(`/invoices/${invoice.id}/pay`, { account_id: accountId, pay_date: payDate });
      toast.success(invoice.direction === "sales" ? "Tahsilat kaydedildi" : "Ödeme kaydedildi");
      onSaved();
    } catch { toast.error("Kaydedilemedi"); }
  };

  const isSales = invoice.direction === "sales";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        data-testid="invoice-pay-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        className="bg-white border border-black w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="label-caps">Fatura</div>
            <h2 className="font-display text-xl font-bold">{isSales ? "Tahsilat" : "Fatura Ödemesi"}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="brutal-card p-4 bg-neutral-50 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="label-caps">Tutar</span>
              <span className={`font-mono-num text-lg font-semibold ${isSales ? "text-emerald-700" : "text-rose-600"}`}>
                {fmtTRY(invoice.amount)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Fatura No</span>
              <span className="font-mono-num">{invoice.invoice_number}</span>
            </div>
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Cari</span>
              <span>{contactName || "—"}</span>
            </div>
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Vade</span>
              <span className="font-mono-num">{fmtDate(invoice.due_date)}</span>
            </div>
          </div>
          <F label={isSales ? "Tahsilat Hesabı" : "Ödeme Hesabı"}>
            <select
              required data-testid="pay-inv-account"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="input bg-white"
            >
              <option value="">— Seçin —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>
          <F label={isSales ? "Tahsilat Tarihi" : "Ödeme Tarihi"}>
            <input
              required type="date" data-testid="pay-inv-date"
              value={payDate}
              onChange={(e) => setPayDate(e.target.value)}
              className="input"
            />
          </F>
          <div className="text-[11px] text-neutral-500 border-l-2 border-black pl-3">
            {isSales
              ? "Seçilen hesapta otomatik bir Gelir işlemi oluşturulacak ve fatura kapatılacak."
              : "Seçilen hesapta otomatik bir Gider işlemi oluşturulacak ve fatura kapatılacak."}
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="pay-inv-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">
            {isSales ? "Tahsilatı Kaydet" : "Ödemeyi Kaydet"}
          </button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}
